import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Loader2, PackagePlus, ArrowDownUp, AlertTriangle, ShoppingCart, Clock } from "lucide-react";

export default function Products() {
  const { companyId, cur, company } = useCompany();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(null);
  const [form, setForm] = useState({ name: "", sku: "", category: "", sale_price: 0, purchase_price: 0, quantity: 0, min_threshold: 0, default_supplier_id: "" });
  const [move, setMove] = useState({ type: "in", quantity: 1, note: "" });
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [reorder, setReorder] = useState(null);
  const [rf, setRf] = useState({ supplier_id: "", qty: 1, unit_price: 0 });
  const [digest, setDigest] = useState({ hour: 7, enabled: true });
  const [fc, setFc] = useState({});

  const load = () => api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setItems(data.products));
  useEffect(() => {
    if (!companyId) return;
    load();
    api.get("/suppliers", { params: { company_id: companyId } }).then(({ data }) => setSuppliers(data.suppliers || []));
    api.get("/inventory/forecast", { params: { company_id: companyId } }).then(({ data }) => {
      const m = {}; (data.forecast || []).forEach((f) => { m[f.id] = f; }); setFc(m);
    }).catch(() => {});
  }, [companyId]);
  useEffect(() => {
    if (company) setDigest({ hour: company.stock_digest_hour ?? 7, enabled: company.stock_digest_enabled ?? true });
  }, [company]);

  const saveDigest = async (next) => {
    setDigest(next);
    try { await api.post(`/companies/${companyId}/stock-digest`, next); toast.success("Réglage du récap enregistré"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openReorder = (p) => {
    const rec = fc[p.id]?.recommended_qty;
    const suggested = rec && rec >= 1 ? rec : Math.max(Math.ceil((p.min_threshold || 0) * 2 - (p.quantity || 0)), 1);
    setRf({ supplier_id: p.default_supplier_id || suppliers[0]?.id || "", qty: suggested, unit_price: p.purchase_price || 0 });
    setReorder(p);
  };
  const submitReorder = async () => {
    try {
      await api.post("/purchase-orders", {
        supplier_id: rf.supplier_id,
        lines: [{ product_id: reorder.id, name: reorder.name, qty: parseFloat(rf.qty) || 1, unit_price: parseFloat(rf.unit_price) || 0, tax_rate: 0 }],
        note: `Réapprovisionnement ${reorder.name}`,
      }, { params: { company_id: companyId } });
      toast.success("Bon de commande créé (brouillon)"); setReorder(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.post("/products", form, { params: { company_id: companyId } }); toast.success("Produit créé"); setOpen(false);
      setForm({ name: "", sku: "", category: "", sale_price: 0, purchase_price: 0, quantity: 0, min_threshold: 0, default_supplier_id: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doMove = async () => {
    try { await api.post("/stock/movements", { product_id: moveOpen.id, ...move, quantity: parseFloat(move.quantity) }, { params: { company_id: companyId } });
      toast.success("Mouvement enregistré"); setMoveOpen(null); setMove({ type: "in", quantity: 1, note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="products-page">
      <PageHeader title="Produits & Stock" subtitle="Catalogue, prix et niveaux de stock." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-product-button"><Plus className="w-4 h-4 mr-1" /> Nouveau produit</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display">Nouveau produit</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
                <div className="space-y-1"><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Prix de vente</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value) || 0 })} data-testid="product-price-input" /></div>
                <div className="space-y-1"><Label>Prix d'achat</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Quantité initiale</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} data-testid="product-qty-input" /></div>
                <div className="space-y-1"><Label>Seuil minimum</Label><Input type="number" value={form.min_threshold} onChange={(e) => setForm({ ...form, min_threshold: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="space-y-1"><Label>Fournisseur préféré (réappro en 1 clic)</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.default_supplier_id} onChange={(e) => setForm({ ...form, default_supplier_id: e.target.value })} data-testid="product-supplier-select">
                  <option value="">Aucun</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving || !form.name} className="rounded-full" data-testid="save-product-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      {items && items.filter((p) => (p.min_threshold || 0) > 0 && (p.quantity || 0) <= (p.min_threshold || 0)).length > 0 && (
        <Card className="p-4 rounded-md border-amber-300 bg-amber-50 shadow-sm mb-5" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 font-display font-semibold text-amber-800 mb-2">
            <AlertTriangle className="w-5 h-5" />
            {items.filter((p) => (p.min_threshold || 0) > 0 && (p.quantity || 0) <= (p.min_threshold || 0)).length} produit(s) à réapprovisionner
          </div>
          <ul className="space-y-1.5 text-sm text-amber-900">
            {items.filter((p) => (p.min_threshold || 0) > 0 && (p.quantity || 0) <= (p.min_threshold || 0)).map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3" data-testid={`low-stock-item-${p.id}`}>
                <span className="min-w-0">
                  • {p.name} — {p.quantity} en stock (seuil {p.min_threshold})
                  {fc[p.id] && (
                    <span className="block text-xs text-amber-700/90 mt-0.5" data-testid={`forecast-${p.id}`}>
                      Vente moy. {fc[p.id].avg_daily_sales}/j · {fc[p.id].days_to_stockout != null ? `rupture dans ${fc[p.id].days_to_stockout} j` : "pas de ventes récentes"}{fc[p.id].recommended_qty > 0 ? ` · reco : commander ${fc[p.id].recommended_qty}` : ""}
                      {fc[p.id].seasonal_hint && <span className="block text-primary font-medium mt-0.5">📈 {fc[p.id].seasonal_hint}</span>}
                    </span>
                  )}
                </span>
                <Button size="sm" variant="outline" className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0" onClick={() => openReorder(p)} data-testid={`reorder-${p.id}`}>
                  <ShoppingCart className="w-3.5 h-3.5 mr-1" />Commander
                </Button>
              </li>
            ))}
          </ul>
        </Card>)}
      <Card className="p-3 rounded-md border-border shadow-sm mb-5 flex flex-wrap items-center gap-3" data-testid="digest-settings">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Clock className="w-4 h-4 text-primary" />Récap stock quotidien par email</div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={digest.enabled} onChange={(e) => saveDigest({ ...digest, enabled: e.target.checked })} data-testid="digest-enabled-toggle" />
          Activé
        </label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          à
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={digest.hour} disabled={!digest.enabled}
            onChange={(e) => saveDigest({ ...digest, hour: parseInt(e.target.value) })} data-testid="digest-hour-select">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h00</option>)}
          </select>
          <span className="text-xs">(heure GMT)</span>
        </div>
      </Card>
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucun produit" hint="Ajoutez vos articles pour suivre le stock." />
          : <Table>
              <TableHeader><TableRow><TableHead>Produit</TableHead><TableHead className="text-right">Prix vente</TableHead><TableHead className="text-right">Stock</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{items.map((p) => {
                const low = (p.quantity || 0) <= (p.min_threshold || 0);
                return (
                  <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}<div className="text-xs text-muted-foreground">{p.sku || "—"}</div></TableCell>
                    <TableCell className="text-right"><Money value={p.sale_price} cur={cur} /></TableCell>
                    <TableCell className="text-right"><Badge className={`border-0 ${low ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{p.quantity}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-full" onClick={() => setMoveOpen(p)} data-testid={`stock-move-${p.id}`}><ArrowDownUp className="w-3.5 h-3.5 mr-1" />Mouvement</Button></TableCell>
                  </TableRow>);
              })}</TableBody>
            </Table>}
      </Card>

      <Dialog open={!!moveOpen} onOpenChange={(o) => !o && setMoveOpen(null)}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><PackagePlus className="w-5 h-5 text-primary" />{moveOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Type de mouvement</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={move.type} onChange={(e) => setMove({ ...move, type: e.target.value })} data-testid="move-type-select">
                <option value="in">Entrée (+)</option><option value="out">Sortie (−)</option><option value="adjustment">Ajustement</option><option value="loss">Perte (−)</option><option value="return">Retour (+)</option>
              </select></div>
            <div className="space-y-1"><Label>Quantité</Label><Input type="number" value={move.quantity} onChange={(e) => setMove({ ...move, quantity: e.target.value })} data-testid="move-qty-input" /></div>
          </div>
          <DialogFooter><Button onClick={doMove} className="rounded-full" data-testid="save-move-button">Valider</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reorder} onOpenChange={(o) => !o && setReorder(null)}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-primary" />Commander — {reorder?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Fournisseur *</Label>
              {suppliers.length === 0
                ? <p className="text-sm text-rose-600">Aucun fournisseur. Créez-en un dans « Fournisseurs » d'abord.</p>
                : <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={rf.supplier_id} onChange={(e) => setRf({ ...rf, supplier_id: e.target.value })} data-testid="reorder-supplier-select">
                    <option value="">—</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Quantité</Label><Input type="number" value={rf.qty} onChange={(e) => setRf({ ...rf, qty: e.target.value })} data-testid="reorder-qty-input" /></div>
              <div className="space-y-1"><Label>Prix d'achat unitaire</Label><Input type="number" value={rf.unit_price} onChange={(e) => setRf({ ...rf, unit_price: e.target.value })} data-testid="reorder-price-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={submitReorder} disabled={!rf.supplier_id} className="rounded-full" data-testid="submit-reorder-button">Créer le bon de commande</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
